import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getHealingTrajectoryAsync } from "../../../app/patientSlice";
import { format } from "date-fns";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import "./healingTrajectory.css";
import styles from "./healingTrajectory.module.scss";
import { BWAT } from "../../../constants";
import { allNullCheck } from "../../../utils";
import { useParams } from "react-router-dom";
import HealingTrajectoryChart from "../../secondary/HealingTrajectoryChart/HealingTrajectoryChart";
import { useLoading } from "../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../constants/loadingKeys";
import { Spinner } from "../../secondary/Loader/Spinner";
import { StatsBar } from "../../secondary/HealingTrajectoryChart/StatsBar";

export function HealingTrajectory({ detailAssessment }) {
  const { assessmentId, patientId } = useParams();
  const dispatch = useDispatch();

  const woundProgressOptions = useSelector((state) => state.patient.woundProgress);
  const { healingTrajectoryInfos, trendLine, mcsdAnalysis, surfaceAreaMetric } = useSelector((state) => state.patient.healingTrajectoryData);
  const userDetailsAlt = useSelector((state) => state.user.userDetails);
  const isLoading = useLoading(LOADING_KEYS.HEALING_TRAJECTORY);

  const [currentAssess, setcurrentAssess] = useState(0);
  const [surfaceArray, setSurfaceArray] = useState([]);
  const [scoreType, setScoreType] = useState("");

  const healTraj = useMemo(() => {
    return healingTrajectoryInfos?.filter((data) => data?.dimensions?.surfaceArea?.value) || [];
  }, [healingTrajectoryInfos]);

  const max = healTraj.length ? healTraj.length - 1 : 0;

  const isStickerBased = detailAssessment?.measurement?.isStickerAvailable === 1 && detailAssessment?.isWoundWithoutPhoto === 0;

  useEffect(() => {
    dispatch(getHealingTrajectoryAsync({ patientId, assessmentId }));
  }, [assessmentId, dispatch, patientId]);

  useEffect(() => {
    if (healTraj.length) setcurrentAssess(max);
  }, [healTraj, max]);

  useEffect(() => {
    if (!healTraj?.length) {
      setSurfaceArray([]);
      return;
    }
    setSurfaceArray(
      healTraj
        .map((d) => {
          const dateObj = new Date(d?.measurementDate);
          return {
            x: d?.weeksFromBaseLine,
            y: d?.dimensions?.surfaceArea?.value,
            date: format(dateObj, "MMMM dd, yyyy hh:mm aa"),
          };
        })
    );
  }, [healTraj]);

  useEffect(() => {
    const score = userDetailsAlt?.customerSettings?.woundAssessmentScore;
    if (score) setScoreType(score);
  }, [userDetailsAlt]);

  const woundHealingPictures =
    healTraj?.length > 0 && healTraj.map((data, index) => <img key={index} src={data.woundPicture} alt="" className="d-none" />);

  const current = healTraj[currentAssess];

  const changeClass = (color) => {
    if (color === "green") {
      return `${styles.closureColor} ${styles.closureColor_green}`
    } else if (color === "red") {
      return `${styles.closureColor} ${styles.closureColor_red}`
    } else {
      return `${styles.closureColor} ${styles.closureColor_grey}`;
    }
  };

  const arrowClass = (direction) => {
    if (!direction) return "";
    return direction === "down" ? `fa-solid fa-down-long ${styles.closureColor_green}` : `fa-solid fa-up-long ${styles.closureColor_red}`;
  };

  const renderPctChange = (dimType) => {
    if (!allNullCheck(dimType?.differencePercentage)) return <span className={styles.closureColor}>Unknown</span>;
    return (
      <span className={changeClass(dimType?.iconColor)}>
        <span style={{ paddingRight: 4 }}>
          <i className={arrowClass(dimType?.arrowDirection)} />
        </span>
        {Number(dimType?.differencePercentage.replace(/^\D+/g, ""))} %
      </span>
    );
  };

  if (isLoading) return <Spinner />;

  if (!healTraj?.length) {
    return (
      <div className="p-3">
        <h5>No Data Available.</h5>
      </div>
    );
  }

  return (
    <>
      {woundHealingPictures}
      <div className={styles.ht_wrapper}>
        <div className={styles.ht_image_col}>
          <div className={styles.image_block} style={{ position: "relative" }}>
            <img src={current?.woundPicture} alt="wound" className="img-fluid" loading="lazy" />
            {healTraj.length > 1 && (
              <div className={styles.rc_outer}>
                <Slider min={0} max={max} onChange={setcurrentAssess} value={currentAssess} className={styles.rc_style_fix} />
              </div>
            )}
          </div>
        </div>

        <div className={styles.mesurement_blk}>
          <div className={styles.ht_header_row}>
            <div className={styles.ht_header_left}>
              <label className={styles.date_heading}>
                {current?.photoTakenon && format(new Date(current.photoTakenon), "MMMM dd, yyyy hh:mm aa")}
              </label>
              <div className="mt-2">
                <label>Status:</label>
                <span>{woundProgressOptions?.find((o) => o.key === current?.woundStage)?.value || "Unknown"}</span>
              </div>
            </div>

            <div className={styles.ht_header_right}>
              <div className={styles.traj_bwat}>
                <span className="d-block">{current?.score ?? 0}</span>
                <label>{scoreType === BWAT ? "BWAT" : scoreType === "push" ? "PUSH" : "—"}</label>
              </div>

              <div className={styles.traj_bwat_clouser}>
                <div className="text-center">
                  % Change <br />
                  {current?.changeInCm ? (
                    current.changeInCm !== "Unknown" ? (
                      <span
                        className={
                          current.iconColor
                            ? current.iconColor === "green"
                              ? `${styles.closureColor} ${styles.closureColor_green} mt-2`
                              : `${styles.closureColor} ${styles.closureColor_red} mt-2`
                            : `${styles.closureColor} ${styles.closureColor_grey} mt-2`
                        }
                      >
                        <span style={{ paddingRight: 4 }}>
                          <i className={arrowClass(current?.arrowDirection)} />
                        </span>
                        {Number(current.changeInCm.replace(/^\D+/g, ""))} cm
                        {isStickerBased && <sup>2</sup>}
                      </span>
                    ) : (
                      <span className={`${styles.closureColor} ${styles.closureColor_grey} mt-2`}>0 cm{isStickerBased && <sup>2</sup>}</span>
                    )
                  ) : (
                    "—"
                  )}
                  {current?.closurePercentage ? (
                    current.closurePercentage !== "Unknown" ? (
                      <span
                        className={
                          current.closurePercentage !== "0"
                            ? current.closurePercentage < "0"
                              ? `${styles.closureColor} ${styles.closureColor_green} ${current.changeInCm !== "0" && "pl-2"}`
                              : `${styles.closureColor} ${styles.closureColor_red} ${current.changeInCm !== "0" && "pl-2"}`
                            : `${styles.closureColor} ${styles.closureColor_grey} ${current.changeInCm !== "0" && "pl-2"}`
                        }
                      >
                        ({current.closurePercentage.replace(/^\D+/g, "")} %)
                      </span>
                    ) : (
                      <span className={`${styles.closureColor} ${styles.closureColor_grey}`}>Unknown</span>
                    )
                  ) : (
                    "—"
                  )}
                </div>
              </div>
            </div>
          </div>

          <StatsBar healTraj={healTraj} currentAssess={currentAssess} mcsdAnalysis={mcsdAnalysis} trendInfo={trendLine} surfaceAreaMetric={surfaceAreaMetric} />

          <div className="my-4">
            {surfaceArray?.length > 0 ? (
              <HealingTrajectoryChart data={surfaceArray} mcsdAnalysis={mcsdAnalysis} trendInfo={trendLine} />
            ) : (
              "No Chart Available."
            )}
          </div>

          <div className="col-12 mb-4">
            <div className="row no-gutters">
              <div className="col-6">
                <p className={styles.traj_heading}>Dimensions</p>
                <div className="row no-gutters">
                  {[
                    { key: "surfaceArea", label: "Surface Area", cls: styles.measure_surface, unit: `cm${isStickerBased ? "²" : ""}` },
                    { key: "length", label: "Length", cls: styles.measure_length, unit: "cm" },
                    { key: "width", label: "Width", cls: styles.measure_width, unit: "cm" },
                    { key: "depth", label: "Depth", cls: styles.measure_items, unit: "cm" },
                    { key: "tunneling", label: "Tunneling", cls: styles.measure_items, unit: "cm" },
                    { key: "undermining", label: "Undermining", cls: styles.measure_items, unit: "cm" },
                  ].map(({ key, label, cls, unit }) => (
                    <div key={key} className={`col-12 pl-0 ${cls}`}>
                      <label>{label}: </label>
                      <span className={styles.ch_bold}>
                        {allNullCheck(current?.dimensions?.[key]?.value) ? `${current.dimensions[key].value} ${unit}` : "Unknown"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="col-6">
                <p className={styles.traj_heading}>% Change</p>
                <div className="row no-gutters">
                  {[
                    { key: "surfaceArea", cls: styles.change_surface },
                    { key: "length", cls: styles.change_length },
                    { key: "width", cls: styles.change_width },
                    { key: "depth", cls: styles.change_items },
                    { key: "tunneling", cls: styles.change_items },
                    { key: "undermining", cls: styles.change_items },
                  ].map(({ key, cls }) => (
                    <div key={key} className={`col-12 pl-0 ${cls}`}>
                      <span className={styles.ch_bold}>{renderPctChange(current?.dimensions?.[key])}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 mb-4">
            <div className="row no-gutters">
              <div className="col-6">
                <p className={styles.traj_heading}>Healing Progression Rate (HPR)</p>
                {[
                  { key: "worstTissueType", label: "Worst Tissue Type (WTT)" },
                  { key: "surfaceAreaValue", label: "Surface Area Value (SAV)" },
                  { key: "intactValue", label: "Intact Value (IV)" },
                ].map(({ key, label }) => (
                  <div key={key} className="col-12 pl-0 mb-2 mt-2">
                    <label>{label}: </label>
                    <span className={styles.ch_bold}>
                      {allNullCheck(current?.healingProgressionRate?.[key]?.value)
                        ? current.healingProgressionRate[key].value !== "N/A"
                          ? current.healingProgressionRate[key].value
                          : "N/A"
                        : "Unknown"}
                    </span>
                  </div>
                ))}
              </div>

              <div className="col-6">
                <p className={styles.traj_heading}>% Change</p>
                {["worstTissueType", "surfaceAreaValue", "intactValue"].map((key) => (
                  <div key={key} className="col-12 pl-0 mb-2 mt-2">
                    <span className={styles.ch_bold}>{renderPctChange(current?.healingProgressionRate?.[key]?.differencePercentage)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
